import React from "react";
import { Link2Icon } from "@radix-ui/react-icons";
import { formatFileSize } from "@/utils/FileSize";
import { getFileExtension, isImageFile } from "@/utils/imageFile";

interface AttachmentPreviewProps {
  attachments: any[];
  onRemove?: (id: string) => void;
  onPreview?: (
    attachment: any,
    fileUrl: string | null,
    fileName: string
  ) => void;
  showRemoveButton?: boolean;
}

export function AttachmentPreview({
  attachments,
  onRemove,
  onPreview,
  showRemoveButton = true,
}: AttachmentPreviewProps) {
  if (attachments.length === 0) return null;

  return (
    <div className="flex flex-wrap gap-3 bg-white/10 p-2">
      {attachments.map((attachment, index) => {
        const fileUrl =
          attachment.url ||
          (attachment.originalFile instanceof File
            ? URL.createObjectURL(attachment.originalFile)
            : null);
        const fileName =
          attachment.name || attachment.filename || `file-${index}`;

        const fileExtension = getFileExtension(fileName);

        const isImage = isImageFile(
          fileName,
          attachment.contentType || attachment.type,
        );

        const isText = ["txt", "md", "json"].includes(fileExtension || "");

        const handleFileClick = (e: React.MouseEvent) => {
          e.stopPropagation();
          onPreview?.(attachment, fileUrl, fileName);
        };

        const handleRemoveAttachment = (e: React.MouseEvent) => {
          e.stopPropagation();
          onRemove?.(attachment.id);
        };

        const isUploading = !attachment.url && showRemoveButton;

        return (
          <div
            key={attachment.id || index}
            className="flex flex-col items-center gap-1 bg-white p-2 rounded-md shadow-sm border max-w-[120px] hover:shadow-md transition-all cursor-pointer transform hover:scale-105 relative"
            onClick={handleFileClick}
          >
            {isUploading && (
              <div className="absolute inset-0 bg-white/70 flex items-center justify-center rounded-md z-10">
                <div className="w-6 h-6 border-2 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
              </div>
            )}
            {showRemoveButton && (
              <button
                className="absolute -top-1 -right-1 bg-gray-400 text-white rounded-full w-5 h-5 flex items-center justify-center text-[10px] z-10 hover:bg-gray-500 transition-colors"
                onClick={handleRemoveAttachment}
              >
                ×
              </button>
            )}

            {isImage && fileUrl && (
              <div className="w-12 h-10 flex items-center justify-center overflow-hidden">
                <img
                  src={fileUrl}
                  className="max-h-full max-w-full object-contain"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "";
                  }}
                />
              </div>
            )}
            {isText && !isImage && (
              <div className="w-12 h-10 flex items-center justify-center bg-blue-50 rounded border border-blue-100">
                <span className="text-blue-600 font-mono text-xs truncate">
                  {fileExtension?.toUpperCase()}
                </span>
              </div>
            )}
            {!isImage && !isText && (
              <div className="w-12 h-10 flex items-center justify-center bg-muted rounded border">
                <Link2Icon className="size-6 text-muted-foreground" />
              </div>
            )}
            <span className="text-xs truncate w-full text-center font-medium">
              {fileName}
            </span>
            {attachment.size && (
              <span className="text-xs text-gray-500">
                {formatFileSize(attachment.size)}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
